import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Phone, Mail, User } from 'lucide-react';
import { FALLBACK_PAGES } from '../data';

export default function ScrapedPage() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Elegant redirect map for old URLs to future-proof the site
  const redirectMap = {
    'kursus': '/kurser',
    'referencer': '/referencer',
    'om-os': '/om-carpa',
    'kundesupport': '/kontakt',
    'reliability-engineering': '/ydelser',
    'korrigerende-maskinservice': '/ydelser',
    'forebyggende-service': '/ydelser',
    'risk-functional-safety': '/ydelser'
  };

  useEffect(() => {
    async function fetchPage() {
      if (!slug) {
        setLoading(false);
        return;
      }

      // If slug is in redirect map, don't fetch, we will redirect
      if (redirectMap[slug]) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.content) {
            setPageData({
              title: data.title?.da || data.title || '',
              meta_description: data.meta_description?.da || data.meta_description || '',
              content: data.content?.da || data.content || ''
            });
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error(`Error fetching page ${slug} from CMS:`, err);
      }

      // Fallback
      if (FALLBACK_PAGES[slug]) {
        setPageData(FALLBACK_PAGES[slug]);
      } else {
        setPageData(null);
      }
      setLoading(false);
    }

    fetchPage();
  }, [slug]);

  // Perform redirect if slug is in redirect map
  if (slug && redirectMap[slug]) {
    return <Navigate to={redirectMap[slug]} replace />;
  }

  if (loading) {
    return (
      <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#0e1f2f]/10 border-t-[#3c8888]" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen py-24 text-center">
        <div className="mx-auto max-w-md px-6 space-y-6">
          <h1 className="text-3xl font-bold">Siden blev ikke fundet</h1>
          <p className="text-stone-500">Den side, du leder efter, findes ikke eller er blevet flyttet.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#3c8888] hover:text-[#3c8888]/80 font-bold">
            <ArrowLeft className="h-4 w-4" />
            <span>Gå til forsiden</span>
          </Link>
        </div>
      </div>
    );
  }

  const title = pageData.title || `${slug} - CARPA ApS`;
  const desc = pageData.meta_description || "CARPA ApS - Commissioning, Operational Readiness, Technical Leadership og handover.";

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen py-16 md:py-24">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      <div className="mx-auto max-w-4xl px-6">
        {/* Back Link */}
        <div className="mb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-stone-500 hover:text-[#3c8888] transition-colors font-bold"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Tilbage til forsiden</span>
          </Link>
        </div>

        {/* Article Wrapper */}
        <article className="rounded-xl border border-[#0e1f2f]/10 bg-white p-6 md:p-12 shadow-sm">
          <div
            className="
              [&_h1]:text-3xl [&_h1]:sm:text-4xl [&_h1]:font-extrabold [&_h1]:text-[#0e1f2f] [&_h1]:mb-8 [&_h1]:tracking-tight
              [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-bold [&_h2]:text-[#0e1f2f] [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:tracking-tight [&_h2]:border-b [&_h2]:border-[#0e1f2f]/10 [&_h2]:pb-3
              [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[#0e1f2f] [&_h3]:mt-8 [&_h3]:mb-4
              [&_p]:text-stone-600 [&_p]:leading-relaxed [&_p]:mb-6 [&_p]:text-sm [&_p]:sm:text-base
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2.5 [&_ul]:mb-8 [&_ul]:text-sm [&_ul]:sm:text-base [&_ul]:text-stone-600
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2.5 [&_ol]:mb-8 [&_ol]:text-sm [&_ol]:sm:text-base [&_ol]:text-stone-600
              [&_li]:text-stone-600
              [&_a]:text-[#3c8888] [&_a]:underline [&_a]:hover:text-[#3c8888]/80 [&_a]:transition-colors [&_a]:font-bold
              [&_img]:rounded-lg [&_img]:border [&_img]:border-[#0e1f2f]/10 [&_img]:my-8 [&_img]:max-w-full [&_img]:h-auto [&_img]:mx-auto [&_img]:shadow-sm
              [&_strong]:text-[#0e1f2f] [&_strong]:font-bold
              [&_hr]:border-[#0e1f2f]/10 [&_hr]:my-10
              [&_blockquote]:border-l-4 [&_blockquote]:border-[#3c8888] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-stone-500 [&_blockquote]:my-6
            "
            dangerouslySetInnerHTML={{ __html: pageData.content }}
          />

          {/* Clean Contact Box for Laust Kildevæld */}
          <div className="mt-16 pt-10 border-t border-[#0e1f2f]/10">
            <h2 className="text-2xl font-bold text-[#0e1f2f] mb-8 flex items-center gap-3">
              <span className="h-6 w-1 bg-[#3c8888]" />
              <span>Kontakt os</span>
            </h2>
            
            <div className="bg-[#f7f8fa] border border-[#0e1f2f]/10 rounded-xl p-6 shadow-sm max-w-md">
              <div className="font-bold text-[#0e1f2f] text-lg mb-3 flex items-center gap-2">
                <User className="h-5 w-5 text-[#3c8888]" />
                <span>Laust Kildevæld</span>
              </div>
              <p className="text-xs text-stone-500 font-mono mb-4">Stifter | Maskinmester | Senior Consultant</p>
              <ul className="space-y-2 pl-4 border-l-2 border-[#0e1f2f]/10 list-none">
                <li className="text-sm text-stone-600 flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 text-stone-400" />
                  <a href="tel:+4553636223" className="hover:text-[#3c8888] transition-colors whitespace-nowrap font-medium">
                    +45 53 63 62 23
                  </a>
                </li>
                <li className="text-sm text-stone-600 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-stone-400" />
                  <a href="mailto:llk@carpa.dk" className="hover:text-[#3c8888] transition-colors whitespace-nowrap font-medium">
                    llk@carpa.dk
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
